import { useEffect, useMemo, useRef, useState } from 'react';
import { ApiError } from '../../api/client';
import { Icon } from './Icon';
import {
  getTemplates, getTemplate, createTemplate, updateTemplate,
  archiveTemplate, restoreTemplate, duplicateTemplate, getTemplateCounts,
  type TemplateType, type TemplateListItem,
} from '../../api/marketing';
import { SandboxHtml } from '../../shell/SandboxHtml';
import { confirmDialog, noticeDialog } from '../../shell/dialogs';
import { ArcDialog, ArcPage, ArcPageHeader, ArcPanel, ArcSelect, ArcTabs, ArcToolbar } from '../../components/arc/primitives/arc-primitives';
import { Button } from '../../components/arc/button/button';
import { PageState } from '../../components/arc/page-state/page-state';
import './marketing-templates-arc.css';

type Field = { key: string; label: string; type: 'text' | 'textarea' | 'select' | 'number'; required?: boolean; rows?: number; code?: boolean; options?: [string, string][]; col?: number };

const SCHEMA: Record<TemplateType, Field[]> = {
  mail: [
    { key: 'name', label: 'Název (interní)', type: 'text', required: true, col: 8 },
    { key: 'lang', label: 'Jazyk / země', type: 'select', options: [['cs', '🇨🇿 CS / CZ'], ['sk', '🇸🇰 SK'], ['ro', '🇷🇴 RO']], col: 4 },
    { key: 'subject', label: 'Předmět e-mailu', type: 'text', required: true, col: 12 },
    { key: 'purpose', label: 'Účel (interní popis)', type: 'text', col: 12 },
    { key: 'body_html', label: 'HTML obsah', type: 'textarea', code: true, rows: 14, required: true, col: 12 },
    { key: 'body_plain', label: 'Plain-text fallback', type: 'textarea', rows: 5, col: 12 },
  ],
  lp: [
    { key: 'name', label: 'Název', type: 'text', required: true, col: 8 },
    { key: 'country', label: 'Země', type: 'select', options: [['', '-'], ['CZ', '🇨🇿 CZ'], ['SK', '🇸🇰 SK'], ['RO', '🇷🇴 RO']], col: 4 },
    { key: 'slug', label: 'Slug (URL fragment)', type: 'text', required: true, col: 6 },
    { key: 'kind', label: 'Druh', type: 'select', options: [['lp', 'LP'], ['fragment', 'Fragment']], col: 6 },
    { key: 'description', label: 'Popis (interní)', type: 'text', col: 12 },
    { key: 'hero_image_url', label: 'Hero image URL', type: 'text', col: 12 },
    { key: 'preview_url', label: 'Preview URL', type: 'text', col: 12 },
    { key: 'html', label: 'HTML šablona', type: 'textarea', code: true, rows: 14, col: 12 },
  ],
  modal: [
    { key: 'name', label: 'Název (interní)', type: 'text', required: true, col: 6 },
    { key: 'code', label: 'Code', type: 'text', col: 6 },
    { key: 'country', label: 'Země', type: 'select', options: [['', '-'], ['CZ', '🇨🇿'], ['SK', '🇸🇰'], ['RO', '🇷🇴']], col: 4 },
    { key: 'theme', label: 'Téma', type: 'select', options: [['light', 'Světlé'], ['dark', 'Tmavé']], col: 4 },
    { key: 'priority', label: 'Priorita (0-100)', type: 'number', col: 4 },
    { key: 'title', label: 'Titulek modalu', type: 'text', required: true, col: 12 },
    { key: 'body', label: 'Tělo (HTML)', type: 'textarea', code: true, rows: 10, col: 12 },
    { key: 'image_url', label: 'Obrázek URL', type: 'text', col: 12 },
    { key: 'cta_text', label: 'CTA text', type: 'text', col: 6 },
    { key: 'cta_url', label: 'CTA URL', type: 'text', col: 6 },
    { key: 'dismiss_text', label: '"Zavřít" text', type: 'text', col: 12 },
  ],
  notif: [
    { key: 'name', label: 'Název (interní)', type: 'text', required: true, col: 6 },
    { key: 'code', label: 'Code', type: 'text', col: 6 },
    { key: 'country', label: 'Země', type: 'select', options: [['', '-'], ['CZ', '🇨🇿'], ['SK', '🇸🇰'], ['RO', '🇷🇴']], col: 4 },
    { key: 'message', label: 'Zpráva notifikace', type: 'textarea', rows: 4, required: true, col: 12 },
    { key: 'url', label: 'Odkaz po kliknutí', type: 'text', col: 12 },
  ],
  sms: [
    { key: 'name', label: 'Název (interní)', type: 'text', required: true, col: 6 },
    { key: 'code', label: 'Code', type: 'text', col: 6 },
    { key: 'country', label: 'Země', type: 'select', options: [['', '-'], ['CZ', '🇨🇿'], ['SK', '🇸🇰'], ['RO', '🇷🇴']], col: 4 },
    { key: 'sender', label: 'Odesílatel (sender ID)', type: 'text', col: 8 },
    { key: 'body', label: 'Tělo SMS', type: 'textarea', rows: 5, required: true, col: 12 },
    { key: 'notes', label: 'Poznámka (interní)', type: 'textarea', rows: 3, col: 12 },
  ],
};

const TABS: { key: TemplateType; label: string }[] = [
  { key: 'mail', label: 'E-mail' },
  { key: 'lp', label: 'Landing pages' },
  { key: 'modal', label: 'Modaly' },
  { key: 'notif', label: 'Notifikace' },
  { key: 'sms', label: 'SMS' },
];
const isTemplateType = (value: string | null): value is TemplateType => TABS.some((item) => item.key === value);
const BRAND = '#e37921';

const FLAG: Record<string, string> = { CZ: '🇨🇿', CS: '🇨🇿', SK: '🇸🇰', RO: '🇷🇴' };
const flagFor = (c: unknown): string => (c ? FLAG[String(c).slice(0, 2).toUpperCase()] ?? '' : '');

const PREVIEW_VARS: Record<string, string> = {
  GREETING: 'Zdravíme Vás', VOCATIVE: 'Jméno', P1: 'Jméno',
  FIRST_NAME: 'Jan', LAST_NAME: 'Novák', NAME: 'Jan Novák', EMAIL: 'jan.novak@example.cz',
};
function renderTT(s: unknown): string {
  if (s == null) return '';
  let out = String(s);
  out = out.replace(/\[%\s*IF\s+shared\.(\w+)\s*-?%\]([\s\S]*?)(?:\[%\s*ELSE\s*-?%\]([\s\S]*?))?\[%\s*END\s*-?%\]/g,
    (_m, k, a, b) => (PREVIEW_VARS[k] ? a : (b || '')));
  out = out.replace(/\[%\s*shared\.(\w+)\s*-?%\]/g, (_m, k) => PREVIEW_VARS[k] ?? `{${k}}`);
  out = out.replace(/\[SIGNOUT\]/g, '#');
  return out;
}

const params = () => new URLSearchParams(window.location.search);
function setUrl(next: Record<string, string | null>) {
  const url = new URL(window.location.href);
  for (const [k, v] of Object.entries(next)) { if (v == null) url.searchParams.delete(k); else url.searchParams.set(k, v); }
  window.history.replaceState({}, '', url.toString());
}

const SAMPLE_VALS: Record<string, string> = { VOCATIVE: 'Danieli', P1: 'Danieli', FIRST_NAME: 'Daniel', LAST_NAME: 'Trojan', EMAIL: 'daniel@example.com', GREETING: 'Dobrý den' };
function sampleVars(s: string): string {
  return String(s ?? '')
    .replace(/\[%\s*shared\.([A-Z0-9_]+)\s*-?%\]/gi, (_m, k: string) => SAMPLE_VALS[k] ?? (k.endsWith('_URL') ? '#' : '…'))
    .replace(/href\s*=\s*(["'])\s*\[SIGNOUT\]\s*\1/gi, 'href="#"')
    .replace(/\[SIGNOUT\]/g, '#');
}

export function MarketingSablonyPage() {
  const init = params();
  const [tab, setTab] = useState<TemplateType>(isTemplateType(init.get('tab')) ? init.get('tab') as TemplateType : 'mail');
  const [editId, setEditId] = useState<number | 'new' | null>(() => {
    if (init.get('new') === '1') return 'new';
    const e = init.get('edit'); return e && !Number.isNaN(Number(e)) ? Number(e) : null;
  });

  useEffect(() => { if (!isTemplateType(params().get('tab'))) setUrl({ tab }); }, [tab]);

  if (editId != null) {
    return <TemplateEditor type={tab} id={editId === 'new' ? null : editId}
      onBack={() => { setEditId(null); setUrl({ edit: null, new: null }); }} />;
  }
  return <Library tab={tab} onTab={(t) => { setTab(t); setUrl({ tab: t }); }}
    onEdit={(id) => { setEditId(id); setUrl({ tab, edit: String(id) }); }}
    onNew={() => { setEditId('new'); setUrl({ tab, new: '1' }); }} />;
}

function Thumb({ t }: { t: TemplateListItem }) {
  const box: React.CSSProperties = { width: 76, height: 52, borderRadius: 9, overflow: 'hidden', border: '1px solid var(--bs-border-color)', background: '#fff', flexShrink: 0 };
  if (t.thumbnail_kind === 'html' && t.thumbnail_value) {
    return <div className="template-thumb" style={box}><iframe title="náhled" sandbox="allow-same-origin" srcDoc={sampleVars(t.thumbnail_value)} style={{ width: 800, height: 548, border: 0, transform: 'scale(0.095)', transformOrigin: 'top left', pointerEvents: 'none' }} /></div>;
  }
  if (t.thumbnail_kind === 'image' && t.thumbnail_value) {
    return <div className="template-thumb" style={box}><img src={t.thumbnail_value} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }} /></div>;
  }
  return <div className="template-thumb template-thumb-text" style={box}>{t.thumbnail_value ?? '-'}</div>;
}

function TemplatePreview({ type, data }: { type: TemplateType; data: Record<string, unknown> }) {
  const s = (k: string) => (data[k] == null ? '' : String(data[k]));
  const r = (k: string) => renderTT(data[k]);
  if (type === 'mail') return (<><div className="mb-2 small">Předmět: <strong>{r('subject')}</strong></div><iframe title="mail" sandbox="allow-same-origin" srcDoc={sampleVars(r('body_html'))} style={{ width: '100%', height: 420, border: '1px solid var(--bs-border-color)', borderRadius: 8, background: '#fff' }} /></>);
  if (type === 'lp') return s('html') ? <iframe title="lp" sandbox="allow-same-origin" srcDoc={sampleVars(r('html'))} style={{ width: '100%', height: 420, border: '1px solid var(--bs-border-color)', borderRadius: 8 }} /> : s('hero_image_url') ? <img src={s('hero_image_url')} alt="" style={{ maxWidth: '100%', borderRadius: 8 }} /> : <div className="text-muted">Bez náhledu.</div>;
  if (type === 'modal') return (
    <div style={{ position: 'relative', maxWidth: 420, margin: '0 auto', background: s('theme') === 'dark' ? '#1f2430' : '#fff', color: s('theme') === 'dark' ? '#fff' : 'inherit', boxShadow: '0 8px 30px rgba(0,0,0,.18)', borderRadius: 14, padding: 24 }}>
      <span style={{ position: 'absolute', top: 14, right: 14, color: '#9ca3af' }}>✕</span>
      {s('image_url') && <img src={s('image_url')} alt="" style={{ width: '100%', borderRadius: 8, marginBottom: 12 }} />}
      <h4 className="mb-2">{r('title')}</h4>
      <SandboxHtml html={sampleVars(r('body'))} css={`body{color:${s('theme') === 'dark' ? '#fff' : '#212529'};font-size:14px}`} />
      {s('cta_text') && <div className="mt-3"><span className="btn btn-primary btn-sm">{s('cta_text')}</span></div>}
      {s('dismiss_text') && <div className="mt-1"><span className="small text-muted" style={{ cursor: 'pointer' }}>{s('dismiss_text')}</span></div>}
    </div>
  );
  if (type === 'notif') return (
    <div style={{ maxWidth: 420, margin: '0 auto', display: 'flex', gap: 12, padding: 14, borderRadius: 12, background: '#fafbfc', border: '1px solid var(--bs-border-color)' }}>
      <div style={{ width: 36, height: 36, borderRadius: 9, background: BRAND, color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}><Icon name="bell" size={18} /></div>
      <div><div className="fw-semibold">Rondo</div><div className="small">{r('message')}</div>{s('url') && <div className="small text-primary mt-1">{s('url')}</div>}</div>
    </div>
  );
  return (
    <div style={{ maxWidth: 360, margin: '0 auto' }}>
      <div className="small text-muted mb-1">Od: {s('sender') || 'Rondo'}</div>
      <div style={{ maxWidth: 340, background: '#e9f5ff', borderRadius: '14px 14px 14px 4px', padding: '10px 14px', fontSize: '.9rem' }}>{r('body')}</div>
      {s('notes') && <div className="small text-muted mt-2">{s('notes')}</div>}
    </div>
  );
}

function Library({ tab, onTab, onEdit, onNew }: { tab: TemplateType; onTab: (t: TemplateType) => void; onEdit: (id: number) => void; onNew: () => void }) {
  const [list, setList] = useState<TemplateListItem[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [country, setCountry] = useState('');
  const [showArchived, setShowArchived] = useState(false);
  const [q, setQ] = useState('');
  const [qDebounced, setQDebounced] = useState('');
  const [preview, setPreview] = useState<TemplateListItem | null>(null);
  const [counts, setCounts] = useState<Record<string, number>>({});

  useEffect(() => { const t = setTimeout(() => setQDebounced(q), 300); return () => clearTimeout(t); }, [q]);
  useEffect(() => { getTemplateCounts().then(setCounts).catch(() => undefined); }, [list]);

  function reload() {
    setList(null);
    setError(null);
    getTemplates(tab, { country: country || undefined, all: showArchived, q: qDebounced || undefined })
      .then((d) => setList(d.templates))
      .catch((e: unknown) => setError(e instanceof ApiError ? e.message : 'Načtení selhalo'));
  }
  useEffect(reload, [tab, country, showArchived, qDebounced]);

  async function onDuplicate(t: TemplateListItem) { const d = await duplicateTemplate(t.type, t.id); onEdit(d.id); }
  async function onArchive(t: TemplateListItem) { if (!(await confirmDialog({ title: 'Archivovat šablonu?', message: 'Obnovit ji jde přes filtr „Vč. archivovaných“.', confirmLabel: 'Archivovat' }))) return; await archiveTemplate(t.type, t.id); reload(); }
  async function onRestore(t: TemplateListItem) { await restoreTemplate(t.type, t.id); reload(); }

  return (
    <ArcPage className="rondo-template-library rondo-arc-templates">
      <ArcPageHeader eyebrow="MARKETING" title="Šablony" description="Knihovna obsahu pro všechny komunikační kanály" meta={<Button type="button" onClick={onNew}>Nová šablona</Button>} />
      <ArcTabs label="Typ šablony" items={TABS.map((item) => ({ value: item.key, label: <>{item.label}{counts[item.key] != null && <span className="rondo-arc-template-count">{counts[item.key]}</span>}</> }))} value={tab} onChange={onTab} className="rondo-arc-template-tabs" />
      <ArcToolbar label="Filtry šablon" className="rondo-arc-template-toolbar" end={<><input type="search" aria-label="Hledat šablonu" placeholder="Hledat šablonu" value={q} onChange={(e) => setQ(e.target.value)} /><span>{list?.length ?? 0} šablon</span></>}>
        <ArcSelect label="Země" value={country} onChange={setCountry} options={[{ value: '', label: 'Všechny země' }, { value: 'CZ', label: 'CZ' }, { value: 'SK', label: 'SK' }, { value: 'RO', label: 'RO' }]} />
        <ArcSelect label="Stav" value={showArchived ? '1' : '0'} onChange={(value) => setShowArchived(value === '1')} options={[{ value: '0', label: 'Aktivní' }, { value: '1', label: 'Včetně archivovaných' }]} />
        <Button variant="ghost" size="sm" onClick={() => { setCountry(''); setShowArchived(false); setQ(''); }}>Vyčistit</Button>
      </ArcToolbar>

      {error ? <PageState title="Šablony se nepodařilo načíst" message={error} />
        : !list ? <PageState title="Načítám šablony" loading />
        : list.length === 0 ? (
          <ArcPanel className="rondo-arc-template-empty"><Icon name="inbox" size={28} /><p>Žádné šablony.</p><Button size="sm" onClick={onNew}>Nová šablona</Button></ArcPanel>
        ) : (
          <div className="template-library-grid">
              {list.map((t) => (
                <ArcPanel as="article" className="template-library-card rondo-arc-template-card" key={t.id}>
                  <button className="template-card-preview" onClick={() => setPreview(t)} aria-label={`Náhled ${t.name}`}><Thumb t={t} /><span>Zobrazit náhled</span></button>
                  <div className="template-card-body"><div className="template-card-title"><div><strong>{t.name}</strong><code>{t.code}</code></div>{t.archived && <span>Archiv</span>}</div>
                  <div className="template-card-meta"><span>{t.country ? `${flagFor(t.country)} ${t.country}` : 'Všechny trhy'}</span><span>{t.created_at ? new Date(t.created_at).toLocaleDateString('cs-CZ') : '-'}</span>{t.author_name && <span>{t.author_name}</span>}</div>
                    <div className="ms-actions template-card-actions">
                      <Button variant="ghost" size="sm" title="Náhled" aria-label={`Náhled ${t.name}`} onClick={() => setPreview(t)}><Icon name="eye" size={15} /></Button>
                      <Button variant="ghost" size="sm" title="Upravit" aria-label={`Upravit ${t.name}`} onClick={() => onEdit(t.id)}><Icon name="edit-2" size={15} /></Button>
                      <Button variant="ghost" size="sm" title="Duplikovat" aria-label={`Duplikovat ${t.name}`} onClick={() => onDuplicate(t)}><Icon name="copy" size={15} /></Button>
                      {t.archived
                        ? <Button variant="ghost" size="sm" title="Obnovit" aria-label={`Obnovit ${t.name}`} onClick={() => onRestore(t)}><Icon name="rotate-ccw" size={15} /></Button>
                        : <Button variant="ghost" size="sm" title="Archivovat" aria-label={`Archivovat ${t.name}`} onClick={() => onArchive(t)}><Icon name="trash-2" size={15} /></Button>}
                    </div></div>
                </ArcPanel>
              ))}
          </div>
        )}

      {preview && <ArcDialog title={preview.name} description={`${TABS.find((t) => t.key === preview.type)?.label} · ${preview.country || 'všechny trhy'}`} onClose={() => setPreview(null)}><div className="template-preview-canvas"><PreviewLoader item={preview} /></div></ArcDialog>}
    </ArcPage>
  );
}

function PreviewLoader({ item }: { item: TemplateListItem }) {
  const [data, setData] = useState<Record<string, unknown> | null>(null);
  useEffect(() => { getTemplate(item.type, item.id).then(setData).catch(() => setData(null)); }, [item]);
  if (!data) return <div className="text-muted py-3">Načítám…</div>;
  return <TemplatePreview type={item.type} data={data} />;
}

function TemplateEditor({ type, id, onBack }: { type: TemplateType; id: number | null; onBack: () => void }) {
  const fields = SCHEMA[type];
  const [data, setData] = useState<Record<string, unknown>>({});
  const [loading, setLoading] = useState(id != null);
  const [err, setErr] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [pk, setPk] = useState(0);
  const archived = id != null && data.archived === true;
  const savedTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (id == null) { setData({}); return; }
    getTemplate(type, id).then((d) => setData(d)).catch((e: unknown) => setErr(e instanceof ApiError ? e.message : 'Načtení selhalo')).finally(() => setLoading(false));
  }, [type, id]);

  function set(k: string, v: unknown) { setData((d) => ({ ...d, [k]: v })); setSaved(false); }

  async function save() {
    const missing = fields.filter((f) => f.required && (!data[f.key] || String(data[f.key]).trim() === ''));
    if (missing.length) { void noticeDialog({ title: 'Chybí povinná pole', message: `Vyplň: ${missing.map((m) => m.label).join(', ')}` }); return; }
    setSaving(true); setErr(null);
    try {
      const body: Record<string, unknown> = {};
      for (const f of fields) if (data[f.key] !== undefined) body[f.key] = data[f.key];
      if (id == null) {
        const r = await createTemplate(type, body);
        setUrl({ tab: type, new: null, edit: String(r.id) });
        window.location.reload();
      } else {
        await updateTemplate(type, id, body);
        setSaved(true);
        if (savedTimer.current) clearTimeout(savedTimer.current);
        savedTimer.current = setTimeout(() => setSaved(false), 1500);
      }
    } catch (e) { setErr(e instanceof ApiError ? e.message : 'Uložení selhalo'); }
    finally { setSaving(false); }
  }
  async function doArchive() { if (id == null) return; if (!(await confirmDialog({ title: 'Archivovat šablonu?', message: 'Obnovit ji jde přes filtr „Vč. archivovaných“.', confirmLabel: 'Archivovat' }))) return; await archiveTemplate(type, id); onBack(); }
  async function doRestore() { if (id == null) return; await restoreTemplate(type, id); onBack(); }

  if (loading) return <PageState title="Načítám šablonu" loading />;

  return (
    <ArcPage className="template-editor-page rondo-arc-template-editor">
      <Button type="button" variant="ghost" size="sm" className="template-back" onClick={onBack}>Zpět do knihovny</Button>
      <ArcPageHeader eyebrow="MARKETING · ŠABLONY" title={id == null ? `Nová ${TABS.find((t) => t.key === type)?.label} šablona` : String(data.name ?? '')} description={<span className="rondo-arc-template-editor-meta">{TABS.find((t) => t.key === type)?.label}{data.country ? ` · ${String(data.country)}` : ''}{data.code ? ` · ${String(data.code)}` : ''}{archived ? ' · archiv' : ''}</span>} meta={<div className="template-editor-actions">{id != null && !archived && <Button variant="danger" size="sm" onClick={doArchive}>Archivovat</Button>}{id != null && archived && <Button variant="secondary" size="sm" onClick={doRestore}>Obnovit</Button>}<Button size="sm" onClick={save} disabled={saving}>{saved ? 'Uloženo' : saving ? 'Ukládám…' : 'Uložit'}</Button></div>} />
      {err && <PageState title="Šablonu se nepodařilo uložit" message={err} />}

      <div className="template-editor-workspace">
        <ArcPanel className="template-editor-form">
          <div className="rondo-arc-template-form-head"><h2>Údaje šablony</h2><p>Náhled se mění průběžně vedle formuláře.</p></div>
            <div className="row g-2">
              {fields.map((f) => (
                <div key={f.key} className={`col-${f.col ?? 12}`}>
                  {f.type !== 'select' && <label className="form-label small text-muted mb-1" htmlFor={`template-${f.key}`}>{f.label}{f.required && <span className="text-danger"> *</span>}</label>}
                  {f.type === 'textarea'
                    ? <textarea id={`template-${f.key}`} className="form-control form-control-sm" rows={f.rows ?? 4} value={String(data[f.key] ?? '')} onChange={(e) => set(f.key, e.target.value)} style={f.code ? { fontFamily: 'ui-monospace,Menlo,monospace', fontSize: '.82rem' } : undefined} />
                    : f.type === 'select'
                      ? <ArcSelect label={f.label} value={String(data[f.key] ?? (f.options?.[0]?.[0] ?? ''))} onChange={(value) => set(f.key, value)} options={(f.options ?? []).map(([value, label]) => ({ value, label }))} />
                      : <input id={`template-${f.key}`} type={f.type === 'number' ? 'number' : 'text'} className="form-control form-control-sm" value={String(data[f.key] ?? '')} onChange={(e) => set(f.key, f.type === 'number' ? Number(e.target.value) : e.target.value)} />}
                </div>
              ))}
            </div>
        </ArcPanel>
        <ArcPanel as="aside" className="template-editor-preview">
            <div className="template-editor-preview-head">
              <div><strong>Živý náhled</strong><span>{TABS.find((t) => t.key === type)?.label}</span></div>
              <Button type="button" variant="secondary" size="sm" onClick={() => setPk((k) => k + 1)}>Obnovit</Button>
            </div>
            <TemplatePreview key={pk} type={type} data={data} />
        </ArcPanel>
      </div>
    </ArcPage>
  );
}
